import { AuthorizationWrapper } from '@frontend/authorization';
import { EntityType, Rights } from '@frontend/common';
import { AsyncComponent } from '@frontend/components/common';
import { DetailItem, ObjectDetail } from '@frontend/components/detail';
import { DeceasedClient } from '@frontend/deceased/api';
import { DeceasedStatusBadge } from '@frontend/deceased/badge';
import { DeceasedStateSelect } from '@frontend/deceased/select';
import { DeceasedStateName, IDeceased } from '@frontend/deceased/types';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { WorkflowType } from '@frontend/workflow/types';
import { lazy } from 'react';
import { FormattedMessage } from 'react-intl';

import { AdminObjectBadge } from '../../components/admin-object-badge/admin-object-badge.component';
import { useDeceasedDetail } from './deceased-detail.controller';

const ID = 'deceased-detail';
const WorkflowSelectV2 = lazy(() => import('@frontend/workflow/select').then((module) => ({ default: module.WorkflowSelectV2 })));

export const DeceasedDetail = () => {
    const viewProps = useDeceasedDetail();

    return (
        <div id={ID}>
            <ObjectDetail<IDeceased>
                key={viewProps.detailVersion}
                entityType={EntityType.DECEASED}
                reactive
                fetch={(accountId: string, deceasedId: string) => DeceasedClient.fetchDeceased(accountId, deceasedId)}
                urlParamKeys={['accountId', 'deceasedId']}
                baseRoute='/deceased'
                delete={{
                    onDelete: (accountId, deceasedId) => DeceasedClient.deleteDeceased(accountId, deceasedId),
                    propertyName: CommonMessage.OBJECTS.DECEASED.DEFAULT,
                    displayName: (deceased) => deceased.name,
                    requiredPermissions: (obj) => [
                        { item: EntityType.DECEASED, source: EntityType.ACCOUNT, right: Rights.DELETE, uid: obj.account_id },
                        { item: EntityType.DECEASED, source: EntityType.DECEASED, right: Rights.DELETE, uid: obj.id }
                    ]
                }}
                update={{
                    onUpdate: () => viewProps.navigate('../update'),
                    requiredPermissions: (obj) => [
                        { item: EntityType.DECEASED, source: EntityType.ACCOUNT, right: Rights.UPDATE, uid: obj.account_id },
                        { item: EntityType.DECEASED, source: EntityType.DECEASED, right: Rights.UPDATE, uid: obj.id }
                    ]
                }}
                id={ID}
                cardClassName='card mt-3 w-full lg:w-[calc(50%-8px)]'
                objectHeader={(obj) => obj.name}
                order={['name', 'state', 'elys_number', 'date_of_death', 'cremation_timestamp', 'spot_id', 'creation_timestamp', 'update_timestamp']}
                keyOverwrite={
                    new Map([
                        ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS],
                        ['elys_number', CommonMessage.OBJECTS.DECEASED.ELYS_NUMBER],
                        ['date_of_death', CommonMessage.OBJECTS.DECEASED.DATE_OF_DEATH],
                        ['cremation_timestamp', CommonMessage.OBJECTS.DECEASED.CREMATION],
                        ['spot_id', CommonMessage.OBJECTS.SPOT.DEFAULT]
                    ])
                }
                object={viewProps.setObject}
                valueOverwrite={
                    new Map([
                        ['state', (obj: IDeceased) => <DeceasedStatusBadge status={obj.state} />],
                        [
                            'spot_id',
                            (obj: IDeceased) =>
                                obj.spot_id ? (
                                    <AdminObjectBadge
                                        id={obj.spot_id}
                                        entityType={EntityType.SPOT}
                                    />
                                ) : (
                                    <span>/</span>
                                )
                        ]
                    ])
                }>
                {(object) => (
                    <DetailItem
                        identifier='Main'
                        main>
                        <div className='flex w-full flex-col lg:w-[calc(50%-8px)]'>
                            <AuthorizationWrapper
                                requiredPermissions={[{ item: EntityType.WORKFLOW, source: EntityType.ACCOUNT, right: Rights.READ, uid: object.account_id }]}>
                                <div className='card mt-3 w-full'>
                                    <div className='card-header flex justify-between'>
                                        <h4>{CommonMessage.OBJECTS.WORKFLOW.DEFAULT}</h4>
                                        {!viewProps.updateWorkflow ? (
                                            <AuthorizationWrapper
                                                requiredPermissions={[
                                                    { item: EntityType.DECEASED, source: EntityType.ACCOUNT, right: Rights.UPDATE, uid: object.account_id },
                                                    { item: EntityType.DECEASED, source: EntityType.DECEASED, right: Rights.UPDATE, uid: object.id }
                                                ]}>
                                                <button
                                                    id={object.workflow_id ? 'update-button' : 'link-button'}
                                                    className='btn btn-status-info m-0 inline-flex justify-center'
                                                    onClick={viewProps.onUpdateWorkflow}>
                                                    {object.workflow_id ? <ICONS.BUTTON.UPDATE className='me-2' /> : <ICONS.BUTTON.LINK className='me-2' />}
                                                    {object.workflow_id ? CommonMessage.BUTTONS.UPDATE : CommonMessage.BUTTONS.LINK}
                                                </button>
                                            </AuthorizationWrapper>
                                        ) : (
                                            <span>
                                                <button
                                                    id='cancel-button'
                                                    className='btn btn-brand-secondary m-0 me-2'
                                                    onClick={() => viewProps.setUpdateWorkflow(null)}>
                                                    {CommonMessage.BUTTONS.CANCEL}
                                                </button>
                                                <button
                                                    id='link-button'
                                                    className='btn btn-status-success m-0'
                                                    onClick={viewProps.onUpdateWorkflow}>
                                                    <ICONS.BUTTON.LINK className='me-2' />
                                                    {CommonMessage.BUTTONS.SUBMIT}
                                                </button>
                                            </span>
                                        )}
                                    </div>
                                    {!viewProps.updateWorkflow ? (
                                        <div className='card-body'>
                                            {object.workflow_id ? (
                                                <AdminObjectBadge
                                                    entityType={EntityType.WORKFLOW}
                                                    id={object.workflow_id}
                                                />
                                            ) : (
                                                <p>
                                                    <FormattedMessage
                                                        id='admin.deceased.deceased-detail.no-workflow'
                                                        defaultMessage='No workflow is assigned to this deceased'
                                                        description='No workflow is assigned to this deceased message on the deceased detail page'
                                                    />
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <div className='card-body'>
                                            <AsyncComponent>
                                                <WorkflowSelectV2
                                                    queryParams={{ account_id: object.account_id, type: WorkflowType.DECEASED }}
                                                    onChange={(id: string | null) =>
                                                        viewProps.setUpdateWorkflow({ ...viewProps.updateWorkflow, workflow_id: id })
                                                    }
                                                    value={viewProps.updateWorkflow.workflow_id}
                                                    isClearable
                                                />
                                            </AsyncComponent>
                                        </div>
                                    )}
                                </div>
                            </AuthorizationWrapper>
                            {object.workflow_id && (
                                <AuthorizationWrapper
                                    requiredPermissions={[
                                        { item: EntityType.DECEASED, source: EntityType.ACCOUNT, right: Rights.STATE, uid: object.account_id },
                                        { item: EntityType.DECEASED, source: EntityType.DECEASED, right: Rights.STATE, uid: object.id }
                                    ]}>
                                    <div className='card mt-3 w-full'>
                                        <div className='card-header flex justify-between'>
                                            <h4>{CommonMessage.OBJECTS.WORKFLOW.CHANGE_STATE}</h4>
                                            {!viewProps.updateState ? (
                                                <button
                                                    className='btn btn-status-warning'
                                                    id='change-state-button'
                                                    onClick={viewProps.onUpdateState}>
                                                    <ICONS.BUTTON.CHANGE_STATE className='me-2' />
                                                    {CommonMessage.BUTTONS.CHANGE_STATE}
                                                </button>
                                            ) : (
                                                <span>
                                                    <button
                                                        id='cancel-button'
                                                        className='btn btn-brand-secondary me-2'
                                                        onClick={() => viewProps.setUpdateState(null)}>
                                                        {CommonMessage.BUTTONS.CANCEL}
                                                    </button>
                                                    <button
                                                        className='btn btn-status-success'
                                                        id='submit-button'
                                                        onClick={viewProps.onUpdateState}>
                                                        <ICONS.BUTTON.CHANGE_STATE className='me-2' />
                                                        {CommonMessage.BUTTONS.SUBMIT}
                                                    </button>
                                                </span>
                                            )}
                                        </div>
                                        {!viewProps.updateState ? (
                                            <div className='card-body'>
                                                <DeceasedStatusBadge status={object.state} />
                                            </div>
                                        ) : (
                                            <div className='card-body'>
                                                <DeceasedStateSelect
                                                    value={viewProps.updateState.state}
                                                    states={Object.values(DeceasedStateName).filter((state) => state !== DeceasedStateName.ANY)}
                                                    onSelect={(value) =>
                                                        viewProps.setUpdateState({
                                                            ...viewProps.updateState,
                                                            state: value?.value as DeceasedStateName | undefined
                                                        })
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                </AuthorizationWrapper>
                            )}
                        </div>
                    </DetailItem>
                )}
            </ObjectDetail>
        </div>
    );
};
